// Shared helpers for the Circularity exports (per-card PNG/CSV and the page export).
// Every timestamp is written in the viewer's browser timezone, the same one the
// cards send to the backend as ?tz=, so an exported file matches what's on screen.

export const TZ =
    (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Europe/Amsterdam';

const pad = (n) => String(n).padStart(2, '0');

// "2026-10-08 22:21:05" in the viewer's timezone.
export function fmtViewerTime(value, { seconds = true } = {}) {
    if (value === null || value === undefined || value === '' || value === '-') return '-';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d)) return String(value);
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hour = parts.hour === '24' ? '00' : parts.hour;
    const date = `${parts.year}-${parts.month}-${parts.day}`;
    return seconds ? `${date} ${hour}:${parts.minute}:${parts.second}` : `${date} ${hour}:${parts.minute}`;
}

// "2026-10-08" in the viewer's timezone, for file names.
export function fileDate(d = new Date()) {
    return fmtViewerTime(d, { seconds: false }).slice(0, 10);
}

export const slug = (s) => String(s || 'export').replace(/₂/g, '2').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase();

const toDate = (v) => (v instanceof Date ? v : new Date(v));

// Human-readable period for the header of an export.
export function describePeriod({ period, year, customDateRange, isCustomDateRange }) {
    const r = customDateRange?.[0];
    if (isCustomDateRange && r?.startDate && r?.endDate) {
        return `${fmtViewerTime(toDate(r.startDate), { seconds: false })} to ${fmtViewerTime(toDate(r.endDate), { seconds: false })}`;
    }
    if (period === 'year' && year) return `year ${year}`;
    return period || 'N/A';
}

const q = (cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`;

// meta: { period, bins, ... } -> "# Key: value" lines.
export function metaLines(title, meta = {}) {
    const bins = Array.isArray(meta.bins) ? meta.bins.join(', ') : (meta.bins || 'N/A');
    const lines = [
        `# ${title}`,
        `# Exported: ${fmtViewerTime(new Date())} (${TZ})`,
        `# Period: ${meta.period || 'N/A'}`,
        `# Bins: ${bins}`,
    ];
    if (meta.wasteType) lines.push(`# Waste type: ${meta.wasteType}`);
    return lines;
}

// One CSV section: heading + header row + rows (no meta lines).
export function csvSection(title, headers, rows) {
    return [
        `=== ${title} ===`,
        headers.map(q).join(','),
        ...rows.map(r => r.map(q).join(',')),
    ].join('\n');
}

// A complete single-chart CSV (meta lines + the section).
export function buildCSV(title, meta, headers, rows) {
    return [...metaLines(title, meta), '', headers.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\n');
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadCSV(csv, filename) {
    // BOM so Excel reads UTF-8 (CO₂, –, etc.) correctly.
    downloadBlob(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

export const isDarkNow = () =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

export const exportBg = () => (isDarkNow() ? '#100c0c' : '#ffffff');

// Render a DOM node to a PNG data URL. Anything with the "export-controls"
// class (the Export button itself, etc.) is left out of the picture.
export async function nodeToPngDataUrl(node) {
    const htmlToImage = await import('html-to-image');
    return htmlToImage.toPng(node, {
        backgroundColor: exportBg(),
        pixelRatio: 2,
        cacheBust: true,
        filter: (n) => !(n.classList && n.classList.contains('export-controls')),
    });
}

export async function downloadNodePNG(node, filename) {
    const dataUrl = await nodeToPngDataUrl(node);
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
}